// src/router/ProtectedRoute.jsx
// =========================================================================================
// |                       ProtectedRoute.jsx - THE AUTHENTICATION GATEKEEPER                |
// |                       COMPLETE, FINAL, AND ARCHITECTURALLY SOUND                      |
// =========================================================================================

import React from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import LoadingScreen from '../components/common/LoadingScreen.jsx';

/**
 * A robust ProtectedRoute component that handles the three critical states of authentication:
 * 1. LOADING: While Firebase/auth provider is determining the user's status.
 * 2. UNAUTHENTICATED: When the auth check is complete and no user is found.
 * 3. AUTHENTICATED: When the auth check is complete and a user is present.
 *
 * This approach prevents the common "flicker" problem where a logged-in user briefly
 * sees the login page on a refresh before the app recognizes their session.
 *
 * @param {object} props - The component props.
 * @param {React.ReactNode} props.children - The component to render if the user is authenticated.
 * @returns {React.ReactElement} The loading screen, a redirect, or the protected child component.
 */
export const ProtectedRoute = ({ children }) => {
  // Destructure both the user object and the loading state from our custom UserContext hook.
  const { user, loading: isAuthLoading } = useUser();
  const location = useLocation();

  // --- STATE 1: LOADING ---
  // While the authentication state is being determined (isAuthLoading is true),
  // we must display a loading indicator. This is the most crucial step to prevent
  // a premature redirect to the login page for an already authenticated user.
  if (isAuthLoading) {
    return <LoadingScreen type="page" customMessage="Verifying authentication..." />;
  }

  // --- STATE 2: UNAUTHENTICATED ---
  // Once loading is complete (isAuthLoading is false), if there is still no user object,
  // we know for certain the user is not logged in. We must redirect them.
  if (!user) {
    console.log('[ProtectedRoute] Access Denied. User not authenticated. Redirecting to /login.');
    
    // The <Navigate> component from React Router v6 is the standard way to perform redirects.
    // - `to="/login"`: Specifies the destination.
    // - `state={{ from: location }}`: Passes the current location (the page the user tried to access)
    //   to the login page. This allows us to redirect them back after a successful login.
    // - `replace`: Replaces the current entry in the history stack instead of pushing a new one.
    //   This prevents the user from clicking the "back" button and returning to the protected
    //   route they were just redirected from.
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // --- STATE 3: AUTHENTICATED ---
  // If the loading is complete and a user object exists, the user is authenticated.
  // We can safely render the protected content passed as `children`.
  return children;
};